import React, { useCallback, useEffect, useState } from "react";
import { ShieldOff, ArrowRight } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { asSentence, isWithheld } from "../utils/aiFleetPortfolio";

const ACTION_LABEL = {
  DisableEntraIdentity: "Disable Entra identity",
  QuarantineAwsRole: "Quarantine AWS role",
  DisableAnthropicWorkspaceKeys: "Deactivate Anthropic workspace keys",
  DeleteOpenAiProjectKeys: "Delete OpenAI project keys",
  DisableGcpServiceAccount: "Disable Google Cloud service account",
};

/**
 * Org-wide response actions awaiting approval, surfaced above the inventory so an approver who
 * lives on this dashboard (rather than My Tasks) sees the queue. Deciding happens on the asset's
 * Response panel — this banner routes there rather than duplicating the approve/reject controls
 * and their typed confirm. Renders nothing when the queue is empty or the viewer cannot decide (reported as not applicable,
 * never as an empty queue).
 */
export default function AiPendingApprovalsBanner({ onOpenAsset, refreshKey, onCountChange, onUnavailable, onNotApplicable, embedded = false }) {
  const apiCall = useApi();
  const { canManageOrganizationRisks, canViewAllAiEstateData } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  // The queue is an org-wide list: a caller below the estate capability would only collect a 403.
  const canDecide = canManageOrganizationRisks(org?.organizationId) && canViewAllAiEstateData(org?.organizationId);

  const [pending, setPending] = useState([]);
  // null = the list was answered (possibly empty); otherwise why it could not be read.
  const [unavailable, setUnavailable] = useState(null);

  const load = useCallback(async () => {
    if (!canDecide) {
      // The queue was never read for this viewer: that is neither "empty" nor "failed". Report
      // a distinct not-applicable state so no caller can turn it into "nothing awaiting approval".
      onNotApplicable?.(true);
      onUnavailable?.(false);
      onCountChange?.(null);
      return;
    }
    onNotApplicable?.(false);
    let actions;
    try {
      actions = await apiCall("GET", "/ai-assets/response-actions");
    } catch (e) {
      actions = null;
    }
    // A failed request and a withheld envelope are both "not answered", never "empty": saying
    // there is nothing awaiting approval when the answer was withheld would be a false statement.
    if (!Array.isArray(actions)) {
      const reason = isWithheld(actions) ? asSentence(actions.reason) : "The pending approvals could not be read.";
      setPending([]);
      setUnavailable(reason);
      onCountChange?.(null);
      onUnavailable?.(true);
      return;
    }
    const requested = actions.filter((a) => a.status === "Requested");
    setPending(requested);
    setUnavailable(null);
    onUnavailable?.(false);
    // The Action Center's badge — reported from the load, so the count and the list can't drift.
    onCountChange?.(requested.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, canDecide]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!canDecide) return null;

  if (unavailable) {
    return (
      <div className={embedded ? "" : "mb-4 border border-amber-300 bg-amber-50 rounded-xl p-3"}>
        <p className="text-sm text-content-secondary" data-testid="approvals-unavailable">
          Response actions awaiting approval are unavailable. {unavailable}
        </p>
      </div>
    );
  }

  if (pending.length === 0) return null;

  // Embedded in the Action Center: the card, header, and count live on the container — only
  // the queue itself renders here.
  return (
    <div className={embedded ? "" : "mb-4 border border-amber-300 bg-amber-50 rounded-xl p-3"}>
      {!embedded && (
        <div className="flex items-center gap-2 mb-2">
          <ShieldOff className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">
            {pending.length} response action{pending.length === 1 ? "" : "s"} awaiting approval
          </span>
        </div>
      )}
      <div className="space-y-1.5">
        {pending.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-sm">
            <span className="text-amber-900 flex-1 min-w-0 truncate">
              <span className="font-medium">{ACTION_LABEL[a.actionType] || a.actionType}</span>
              {" — "}{a.targetName}
              <span className="text-amber-700 text-xs">
                {" "}(requested by {a.requestedByUserName || "unknown"}
                {a.approverUserName ? `, assigned to ${a.approverUserName}` : ""})
              </span>
            </span>
            <button
              onClick={() => onOpenAsset(a.aiAssetId)}
              className="flex items-center gap-1 text-xs px-2.5 py-1 border rounded-lg font-medium bg-white text-amber-800 border-amber-300 hover:bg-amber-600 hover:text-white hover:border-amber-600 active:scale-95 shrink-0"
            >
              Review
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
